import { useState, useEffect, useRef } from "react";
import AnimatedLogo from "../components/AnimatedLogo/AnimatedLogo";
import Header from "../components/Header/Header";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;900&family=Crimson+Pro:ital,wght@0,300;0,400;0,600;1,300&display=swap');

  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

  :root {
    --gold: #d4af37;
    --gold-light: #f7e29c;
    --gold-dark: #a88520;
    --black: #050505;
    --black-2: #0a0a0a;
    --black-3: #0e0e0e;
    --black-4: #141414;
    --white: #f5f0e8;
    --white-dim: rgba(245,240,232,0.7);
  }

  html { scroll-behavior: smooth; }

  body {
    // background: var(--black);
    background: url('/hero-bg.png') no-repeat center center fixed;
    color: var(--white);
    font-family: 'Crimson Pro', Georgia, serif;
     background-size: cover;
    overflow-x: hidden;
  }

  /* === NAVBAR === */
  .navbar {
    position: fixed; top: 0; left: 0; right: 0;
    z-index: 1000;
    display: flex; align-items: center; justify-content: space-between;
    padding: 18px 6%;
    background: rgba(5,5,5,0.85);
    backdrop-filter: blur(18px);
    border-bottom: 1px solid rgba(212,175,55,0.15);
    transition: all 0.4s;
  }
  .navbar.scrolled {
    padding: 12px 6%;
    background: rgba(5,5,5,0.97);
  }
  .nav-logo {
    font-family: 'Cinzel', serif;
    font-size: 22px;
    font-weight: 700;
    color: var(--gold);
    letter-spacing: 2px;
    text-decoration: none;
  }
  .nav-logo span { color: var(--white-dim); font-weight: 400; }
  .nav-links { display: flex; gap: 36px; align-items: center; }
  .nav-links a {
    font-family: 'Crimson Pro', serif;
    font-size: 15px;
    letter-spacing: 1.5px;
    color: var(--white-dim);
    text-decoration: none;
    text-transform: uppercase;
    transition: color 0.3s;
    position: relative;
  }
  .nav-links a::after {
    content: ''; position: absolute; bottom: -4px; left: 0; right: 0;
    height: 1px; background: var(--gold);
    transform: scaleX(0); transition: transform 0.3s;
  }
  .nav-links a:hover { color: var(--gold); }
  .nav-links a:hover::after { transform: scaleX(1); }
  .nav-cta {
    background: linear-gradient(135deg, var(--gold), var(--gold-light));
    color: var(--black) !important;
    padding: 9px 24px;
    border-radius: 2px;
    font-weight: 600;
    font-family: 'Cinzel', serif !important;
    font-size: 13px !important;
    letter-spacing: 2px !important;
  }
  .nav-cta::after { display: none !important; }
  .nav-cta:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(212,175,55,0.4); }

  /* === HERO === */
  .hero {
    min-height: 100vh;
    display: flex; flex-direction: column;
    justify-content: center; align-items: center;
    text-align: center;
    padding: 120px 6% 80px;
    position: relative;
    overflow: hidden;
  }

  .hero-bg {
    position: absolute; inset: 0;
     background: url("https://res.cloudinary.com/dogwkdiny/image/upload/v1773749404/snapedit_1773746022566_iojq3n.png")
    no-repeat center center;
  background-size: cover;
  }

  /* Network SVG background */
  .hero-network {
    position: absolute; inset: 0;
    opacity: 0.12;
    pointer-events: none;
  }

  .hero-badge {
    display: inline-flex; align-items: center; gap: 10px;
    background: rgba(212,175,55,0.08);
    border: 1px solid rgba(212,175,55,0.3);
    padding: 8px 22px;
    border-radius: 40px;
    font-size: 13px;
    letter-spacing: 3px;
    text-transform: uppercase;
    color: var(--gold-light);
    margin-bottom: 36px;
    animation: fadeUp 0.8s ease both;
  }
  .hero-badge-dot {
    width: 7px; height: 7px;
    background: var(--gold);
    border-radius: 50%;
    animation: pulse 2s ease-in-out infinite;
  }
  @keyframes pulse {
    0%,100% { box-shadow: 0 0 0 0 rgba(212,175,55,0.6); }
    50% { box-shadow: 0 0 0 6px rgba(212,175,55,0); }
  }

  .hero h1 {
    font-family: 'Cinzel', serif;
    font-size: clamp(36px, 6vw, 78px);
    font-weight: 900;
    line-height: 1.05;
    color: var(--white);
    letter-spacing: 1px;
    margin-bottom: 24px;
    animation: fadeUp 1s 0.2s ease both;
  }
  .hero h1 .gold-text {
    background: linear-gradient(135deg, var(--gold) 0%, var(--gold-light) 50%, var(--gold) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    display: block;
  }

  .hero-sub {
    font-size: clamp(16px, 2vw, 21px);
    font-weight: 300;
    font-style: italic;
    color: var(--white-dim);
    max-width: 680px;
    line-height: 1.7;
    margin-bottom: 50px;
    animation: fadeUp 1s 0.35s ease both;
  }

  .hero-actions {
    display: flex; gap: 16px; flex-wrap: wrap; justify-content: center;
    animation: fadeUp 1s 0.5s ease both;
    padding-top: 20px;
  }

  .btn-primary {
    padding: 16px 44px;
    background: linear-gradient(135deg, var(--gold), var(--gold-light), var(--gold));
    background-size: 200% 100%;
    color: var(--black);
    font-family: 'Cinzel', serif;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 2.5px;
    text-transform: uppercase;
    border: none;
    border-radius: 2px;
    cursor: pointer;
    transition: all 0.4s;
    box-shadow: 0 0 30px rgba(212,175,55,0.35), inset 0 1px 0 rgba(255,255,255,0.2);
    position: relative; overflow: hidden;
  }
  .btn-primary:hover {
    background-position: right center;
    transform: translateY(-3px);
    box-shadow: 0 12px 40px rgba(212,175,55,0.5);
  }
  .btn-secondary {
    padding: 15px 40px;
    background: transparent;
    color: var(--gold-light);
    font-family: 'Cinzel', serif;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 2px;
    text-transform: uppercase;
    border: 1px solid rgba(212,175,55,0.5);
    border-radius: 2px;
    cursor: pointer;
    transition: all 0.4s;
  }
  .btn-secondary:hover {
    background: rgba(212,175,55,0.08);
    border-color: var(--gold);
    transform: translateY(-3px);
  }
  .btn-ghost {
    padding: 15px 36px;
    background: transparent;
    color: var(--white-dim);
    font-family: 'Crimson Pro', serif;
    font-size: 16px;
    font-weight: 400;
    letter-spacing: 1px;
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 2px;
    cursor: pointer;
    transition: all 0.4s;
    display: flex; align-items: center; gap: 10px;
  }
  .btn-ghost:hover {
    border-color: rgba(255,255,255,0.3);
    color: var(--white);
    transform: translateY(-2px);
  }
  .play-icon {
    width: 22px; height: 22px;
    background: rgba(212,175,55,0.2);
    border: 1px solid rgba(212,175,55,0.5);
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 9px;
  }

  .hero-stats {
    display: flex; gap: 60px; margin-top: 80px;
    animation: fadeUp 1s 0.65s ease both;
  }
  .stat { text-align: center; }
  .stat-number {
    font-family: 'Cinzel', serif;
    font-size: 36px;
    font-weight: 700;
    color: var(--gold);
  }
  .stat-label {
    font-size: 13px;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--white-dim);
    margin-top: 4px;
  }
  .stat-divider {
    width: 1px;
    background: linear-gradient(to bottom, transparent, rgba(212,175,55,0.3), transparent);
  }

  /* Floating nodes animation */
  .node {
    position: absolute;
    border-radius: 50%;
    border: 1px solid rgba(212,175,55,0.3);
    animation: floatNode var(--dur, 6s) ease-in-out infinite;
    animation-delay: var(--delay, 0s);
    pointer-events: none;
  }
  @keyframes floatNode {
    0%,100% { transform: translate(0, 0) scale(1); opacity: 0.4; }
    33% { transform: translate(var(--tx,10px), var(--ty,-15px)) scale(1.05); opacity: 0.7; }
    66% { transform: translate(var(--tx2,-8px), var(--ty2,10px)) scale(0.97); opacity: 0.5; }
  }

  /* === SECTION SHARED === */
  section { padding: 120px 6%; }
  .section-label {
    display: inline-block;
    font-size: 15px;
    letter-spacing: 4px;
    text-transform: uppercase;
    color: var(--gold);
    margin-bottom: 16px;
  }
  .section-title {
    font-family: 'Cinzel', serif;
    font-size: clamp(30px, 4vw, 52px);
    font-weight: 700;
    line-height: 1.1;
    color: var(--white);
    margin-bottom: 20px;
  }
  .section-sub {
    font-size: 18px;
    font-weight: 300;
    font-style: italic;
    color: var(--white-dim);
    line-height: 1.7;
    max-width: 560px;
  }
  .gold-line {
    width: 60px; height: 2px;
    background: linear-gradient(90deg, var(--gold), transparent);
    margin: 24px 0;
  }

  /* === ABOUT === */
  .about {
  background: url('/hero-bg.png') no-repeat center center;
  background-size: cover;
  position: relative;
}
  .about-inner {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 80px;
    align-items: center;
  }
  .about-visual {
    position: relative;
    height: 500px;
    display: flex; align-items: center; justify-content: center;
  }
  .about-ring {
    position: absolute;
    border-radius: 50%;
    border: 1px solid rgba(212,175,55,0.15);
  }
  .about-ring:nth-child(1) { width: 380px; height: 380px; animation: spin 30s linear infinite; }
  .about-ring:nth-child(2) { width: 280px; height: 280px; border-color: rgba(212,175,55,0.2); animation: spin 20s linear infinite reverse; }
  .about-ring:nth-child(3) { width: 180px; height: 180px; border-color: rgba(212,175,55,0.3); animation: spin 15s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
  .about-center {
    width: 110px; height: 110px;
    background: radial-gradient(circle, rgba(212,175,55,0.2), rgba(212,175,55,0.05));
    border: 2px solid var(--gold);
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-family: 'Cinzel', serif;
    font-size: 11px;
    letter-spacing: 2px;
    text-align: center;
    color: var(--gold-light);
    z-index: 1;
  }
  .about-dot {
    position: absolute;
    width: 10px; height: 10px;
    background: var(--gold);
    border-radius: 50%;
    box-shadow: 0 0 12px var(--gold);
  }

  .mv-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 32px; }
  .mv-card {
    background: rgba(212,175,55,0.04);
    border: 1px solid rgba(212,175,55,0.15);
    padding: 24px;
    border-radius: 2px;
    transition: all 0.4s;
  }
  .mv-card:hover {
    background: rgba(212,175,55,0.08);
    border-color: rgba(212,175,55,0.4);
    transform: translateY(-4px);
  }
  .mv-card h4 {
    font-family: 'Cinzel', serif;
    font-size: 13px;
    letter-spacing: 3px;
    color: var(--gold);
    text-transform: uppercase;
    margin-bottom: 10px;
  }
  .mv-card p { font-size: 15px; color: var(--white-dim); line-height: 1.6; }

  .cred-list { margin-top: 28px; display: flex; flex-direction: column; gap: 14px; }
  .cred-item {
    display: flex; align-items: center; gap: 14px;
    font-size: 16px; color: var(--white-dim);
  }
  .cred-icon {
    width: 36px; height: 36px; min-width: 36px;
    background: rgba(212,175,55,0.1);
    border: 1px solid rgba(212,175,55,0.3);
    border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    color: var(--gold);
    font-size: 14px;
  }

  /* === ANIMATIONS === */
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
  }

  /* === RESPONSIVE === */
  @media (max-width: 900px) {
    .nav-links { display: none; }
    .about-inner { grid-template-columns: 1fr; gap: 40px; }
    .about-visual { height: 300px; }
    .hero-stats { gap: 30px; }
    .stat-number { font-size: 26px; }
    .mv-grid { grid-template-columns: 1fr; }
  }
  @media (max-width: 580px) {
    .hero-actions { flex-direction: column; align-items: center; }
    .hero-stats { flex-direction: column; gap: 20px; }
    .stat-divider { width: 40px; height: 1px; }
  }

  
`;

function NetworkSVG() {
  const nodes = [
    { cx: 400, cy: 200, r: 14 },
    { cx: 200, cy: 120, r: 10 },
    { cx: 600, cy: 120, r: 10 },
    { cx: 100, cy: 260, r: 8 },
    { cx: 300, cy: 60, r: 8 },
    { cx: 500, cy: 60, r: 8 },
    { cx: 700, cy: 260, r: 8 },
    { cx: 150, cy: 380, r: 7 },
    { cx: 250, cy: 320, r: 7 },
    { cx: 550, cy: 320, r: 7 },
    { cx: 650, cy: 380, r: 7 },
    { cx: 400, cy: 380, r: 7 },
    { cx: 70, cy: 460, r: 6 },
    { cx: 200, cy: 460, r: 6 },
    { cx: 340, cy: 440, r: 6 },
    { cx: 460, cy: 440, r: 6 },
    { cx: 600, cy: 460, r: 6 },
    { cx: 730, cy: 460, r: 6 },
  ];
  const edges = [
    [0, 1],
    [0, 2],
    [1, 3],
    [1, 4],
    [2, 5],
    [2, 6],
    [3, 7],
    [1, 8],
    [0, 11],
    [2, 9],
    [6, 10],
    [7, 12],
    [7, 13],
    [8, 13],
    [8, 14],
    [11, 14],
    [11, 15],
    [9, 15],
    [9, 16],
    [10, 16],
    [10, 17],
  ];
  return (
    <svg
      viewBox="0 0 800 500"
      xmlns="http://www.w3.org/2000/svg"
      className="hero-network"
    >
      {edges.map(([a, b], i) => (
        <line
          key={i}
          x1={nodes[a].cx}
          y1={nodes[a].cy}
          x2={nodes[b].cx}
          y2={nodes[b].cy}
          stroke="#d4af37"
          strokeWidth="1"
          strokeOpacity="0.6"
        />
      ))}
      {nodes.map((n, i) => (
        <g key={i}>
          <circle
            cx={n.cx}
            cy={n.cy}
            r={n.r + 6}
            fill="rgba(212,175,55,0.06)"
          />
          <circle
            cx={n.cx}
            cy={n.cy}
            r={n.r}
            fill="rgba(212,175,55,0.15)"
            stroke="#d4af37"
            strokeWidth="1"
          />
        </g>
      ))}
    </svg>
  );
}

function AboutVisual() {
  const dotPositions = [
    { top: "10%", left: "50%", size: 10 },
    { top: "30%", left: "12%", size: 8 },
    { top: "30%", left: "88%", size: 8 },
    { top: "70%", left: "20%", size: 7 },
    { top: "70%", left: "80%", size: 7 },
    { top: "90%", left: "50%", size: 9 },
  ];
  return (
    <div className="about-visual">
      <div className="about-ring" />
      <div className="about-ring" />
      <div className="about-ring" />
      <div className="about-center">
        GROW
        <br />
        BIZ
        <br />
        INTL
      </div>
      {dotPositions.map((d, i) => (
        <div
          key={i}
          className="about-dot"
          style={{
            top: d.top,
            left: d.left,
            width: d.size,
            height: d.size,
            transform: "translate(-50%,-50%)",
            boxShadow: `0 0 ${d.size + 4}px #d4af37`,
          }}
        />
      ))}
    </div>
  );
}

export default function GrowBizHero() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const floatingNodes = [
    {
      size: 80,
      top: "15%",
      left: "8%",
      dur: "7s",
      delay: "0s",
      tx: "15px",
      ty: "-20px",
      tx2: "-10px",
      ty2: "12px",
    },
    {
      size: 50,
      top: "25%",
      right: "10%",
      dur: "9s",
      delay: "1s",
      tx: "-12px",
      ty: "-18px",
      tx2: "8px",
      ty2: "15px",
    },
    {
      size: 120,
      bottom: "20%",
      left: "5%",
      dur: "11s",
      delay: "2s",
      tx: "20px",
      ty: "-10px",
      tx2: "-15px",
      ty2: "20px",
    },
    {
      size: 60,
      bottom: "30%",
      right: "8%",
      dur: "8s",
      delay: "0.5s",
      tx: "-18px",
      ty: "-12px",
      tx2: "14px",
      ty2: "8px",
    },
    {
      size: 40,
      top: "60%",
      left: "20%",
      dur: "6s",
      delay: "1.5s",
      tx: "10px",
      ty: "-15px",
      tx2: "-8px",
      ty2: "10px",
    },
  ];

  const credItems = [
    { icon: "🌐", text: "Global business network spanning 3 major cities" },
    { icon: "🔒", text: "Secure, transparent earning platform" },
    { icon: "📊", text: "Real-time income tracking & reporting" },
    { icon: "🤝", text: "Backed by experienced investment professionals" },
    { icon: "⭐", text: "10,000+ active members and growing" },
  ];

  return (
    <>
      <style>{styles}</style>

      {/* NAVBAR */}
      <Header />

      {/* HERO */}
      <div className="hero" id="hero">
        {/* <AnimatedLogo /> */}
        <div className="hero-bg" />
        <NetworkSVG />

        {floatingNodes.map((n, i) => (
          <div
            key={i}
            className="node"
            style={{
              width: n.size,
              height: n.size,
              top: n.top,
              left: n.left,
              right: n.right,
              bottom: n.bottom,
              "--dur": n.dur,
              "--delay": n.delay,
              "--tx": n.tx,
              "--ty": n.ty,
              "--tx2": n.tx2,
              "--ty2": n.ty2,
            }}
          />
        ))}

        <div className="hero-badge">
          <div className="hero-badge-dot" />
          Now Accepting New Members
        </div>

        <h1 className="gold-text">
          Build Your Network
          <span className="gold-text">& Earn Passive Income</span>
        </h1>

        <p className="hero-sub">
          Grow Biz International empowers ambitious individuals to build
          powerful networks, generate recurring income, and create lasting
          financial freedom through Pakistan's most trusted portfolio management
          ecosystem.
        </p>

        <div className="hero-actions">
          <Link to="/join-us" className="btn-primary">
            Join Now
          </Link>
          <Link to="/login" className="btn-secondary">
            Login
          </Link>
          <button className="btn-ghost">
            <div className="play-icon">▶</div>
            Watch How It Works
          </button>
        </div>

        <div className="hero-stats">
          <div className="stat">
            <div className="stat-number">₨2.4B+</div>
            <div className="stat-label">Managed Funds</div>
          </div>
          <div className="stat-divider" />
          <div className="stat">
            <div className="stat-number">10K+</div>
            <div className="stat-label">Active Members</div>
          </div>
          <div className="stat-divider" />
          <div className="stat">
            <div className="stat-number">3</div>
            <div className="stat-label">Major Cities</div>
          </div>
          <div className="stat-divider" />
          <div className="stat">
            <div className="stat-number">8yr+</div>
            <div className="stat-label">Experience</div>
          </div>
        </div>
      </div>

      {/* ABOUT */}
      <section className="about" id="about">
        <div className="about-inner">
          <AboutVisual />

          <div>
            <span className="section-label">Who We Are</span>
            <h2 className="section-title">
              About Grow Biz
              <br />
              International
            </h2>
            <div className="gold-line" />
            <p className="section-sub">
              A premier Portfolio management and real estate investment company
              connecting visionary investors with Pakistan's most lucrative
              property opportunities — and rewarding you for growing our
              community.
            </p>

            <div className="mv-grid">
              <div className="mv-card">
                <h4>Our Mission</h4>
                <p>
                  To democratize wealth creation through accessible real estate
                  investment and collaborative network growth.
                </p>
              </div>
              <div className="mv-card">
                <h4>Our Vision</h4>
                <p>
                  To become Pakistan's leading community-driven investment
                  ecosystem with a global footprint by 2030.
                </p>
              </div>
            </div>

            <div className="cred-list">
              {credItems.map((c, i) => (
                <div className="cred-item" key={i}>
                  <div className="cred-icon">{c.icon}</div>
                  <span>{c.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
